import React, { useState, useEffect, useRef } from 'react';
import { Search, CreditCard, User, Phone, DollarSign, FileText, Info, ArrowLeft, Target, X, Calculator, Loader2, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { usePayContribution } from '../hooks/useContributions.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useContributionDrives } from '../hooks/useContributionDrives.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export default function PayContributionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Opened from a welfare plan (?welfare_plan_id=..): the payment goes to that plan and its own paybill
  const welfarePlan = searchParams.get('welfare_plan_id') ? { id: Number(searchParams.get('welfare_plan_id')), name: searchParams.get('welfare_name') || 'Welfare plan' } : null;
  const queryClient = useQueryClient();
  const { mutate: payContribution, isPending: isPayingContribution } = usePayContribution();
  const { data: membersData } = useMembers();
  const { data: userProfile } = useGetProfile();
  const { data: drivesData } = useContributionDrives();
  const isAdmin = useIsAdmin();

  const members = membersData?.data || [];
  const drives = drivesData?.data || [];

  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedDrives, setSelectedDrives] = useState([]);
  const [driveAmounts, setDriveAmounts] = useState({});
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Real-time STK Push Waiting Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // 'idle' | 'submitting' | 'waiting_pin' | 'completed' | 'failed' | 'timeout'
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [paymentSteps, setPaymentSteps] = useState([
    { key: 'init', label: 'Twachanga Contribution Request', status: 'idle' },
    { key: 'stk', label: 'M-Pesa STK Push Prompt', status: 'idle' },
    { key: 'pin', label: 'Enter M-Pesa PIN on Mobile Phone', status: 'idle' },
    { key: 'confirm', label: 'Twachanga Payment Verification', status: 'idle' },
    { key: 'outcome', label: 'Payment Result', status: 'idle' },
  ]);

  const pollingRef = useRef(null);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Pre-populate phone number for non-admin users
  useEffect(() => {
    if (!isAdmin && userProfile?.member?.phone_number) {
      setPhoneNumber(userProfile.member.phone_number);
    }
  }, [isAdmin, userProfile]);

  // Calculate total amount from drive amounts
  const totalAmount = Object.values(driveAmounts).reduce((sum, amount) => {
    return sum + (parseFloat(amount) || 0);
  }, 0);

  // Update total amount when drive amounts change
  useEffect(() => {
    if (Object.keys(driveAmounts).length > 0) {
      setAmount(totalAmount.toString());
    }
  }, [driveAmounts, totalAmount]);

  const filteredMembers = members.filter((m) =>
    m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.phone_number.includes(memberSearch)
  );

  const handleDriveSelection = (drive) => {
    const isAlreadySelected = selectedDrives.find(d => d.id === drive.id);
    if (isAlreadySelected) {
      setSelectedDrives(selectedDrives.filter(d => d.id !== drive.id));
      const newDriveAmounts = { ...driveAmounts };
      delete newDriveAmounts[drive.id];
      setDriveAmounts(newDriveAmounts);
    } else {
      setSelectedDrives([...selectedDrives, drive]);
      setDriveAmounts({
        ...driveAmounts,
        [drive.id]: drive.amount_per_member ? drive.amount_per_member.toString() : ''
      });
    }
  };

  const removeDrive = (driveId) => {
    setSelectedDrives(selectedDrives.filter(d => d.id !== driveId));
    const newDriveAmounts = { ...driveAmounts };
    delete newDriveAmounts[driveId];
    setDriveAmounts(newDriveAmounts);
  };

  const updateDriveAmount = (driveId, amount) => {
    setDriveAmounts({
      ...driveAmounts,
      [driveId]: amount
    });
  };

  const startStkPolling = (checkoutRequestId) => {
    let attempts = 0;
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      attempts += 1;
      try {
        // 1. Primary check: poll STK status endpoint
        const res = await api.get(`/contributions/stk-status/${checkoutRequestId}`);
        const status = res.data?.status;

        if (status === 'completed') {
          clearInterval(pollingRef.current);
          setPaymentStatus('completed');
          setReceiptNumber(res.data?.receipt_number || checkoutRequestId);
          setPaymentMessage('Payment received and verified successfully!');
          setPaymentSteps([
            { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
            { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
            { key: 'pin', label: 'M-Pesa PIN Entered', status: 'done' },
            { key: 'confirm', label: 'Twachanga Verification', status: 'done' },
            { key: 'outcome', label: 'Payment Completed', status: 'success' },
          ]);
                queryClient.invalidateQueries({ queryKey: ['contributions'] });
                queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
                queryClient.invalidateQueries({ queryKey: ['member-contributions'] });
                queryClient.invalidateQueries({ queryKey: ['member-contribution-stats'] });
                queryClient.invalidateQueries({ queryKey: ['profile'] });
                queryClient.invalidateQueries({ queryKey: ['kit-balance'] });
                queryClient.refetchQueries({ queryKey: ['contributions'] });
                queryClient.refetchQueries({ queryKey: ['contribution-stats'] });
                toast.success('Contribution payment confirmed!');

                setTimeout(() => {
                  setShowPaymentModal(false);
                  navigate('/contributions');
                }, 2000);
          return;
        } else if (status === 'failed') {
          clearInterval(pollingRef.current);
          setPaymentStatus('failed');
          setPaymentError('Payment was cancelled or failed on your phone. Please check your balance or M-Pesa account.');
          setPaymentSteps([
            { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
            { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
            { key: 'pin', label: 'PIN Cancelled / Failed', status: 'failed' },
            { key: 'confirm', label: 'Twachanga Verification', status: 'done' },
            { key: 'outcome', label: 'Payment Failed', status: 'failed' },
          ]);
          return;
        }

        // 2. Secondary check: Every 2 attempts (~5s), check if recent contribution arrived in database
        if (attempts % 2 === 0) {
          try {
            const listRes = await api.get('/contributions?per_page=5');
            const recentList = listRes.data?.data || listRes.data || [];
            if (Array.isArray(recentList) && recentList.length > 0) {
              const latest = recentList[0];
              const latestTime = new Date(latest.created_at || latest.contribution_date).getTime();
              const nowTime = new Date().getTime();
              // If latest contribution was created in the last 90 seconds
              if (!isNaN(latestTime) && (nowTime - latestTime) < 90000) {
                clearInterval(pollingRef.current);
                setPaymentStatus('completed');
                setReceiptNumber(latest.reference || checkoutRequestId);
                setPaymentMessage('Payment received and verified successfully!');
                setPaymentSteps([
                  { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
                  { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
                  { key: 'pin', label: 'M-Pesa PIN Entered', status: 'done' },
                  { key: 'confirm', label: 'Twachanga Verification', status: 'done' },
                  { key: 'outcome', label: 'Payment Completed', status: 'success' },
                ]);
                queryClient.invalidateQueries({ queryKey: ['contributions'] });
                queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
                queryClient.invalidateQueries({ queryKey: ['profile'] });
                queryClient.invalidateQueries({ queryKey: ['kit-balance'] });
                toast.success('Contribution payment confirmed!');

                setTimeout(() => {
                  setShowPaymentModal(false);
                  navigate('/contributions');
                }, 2000);
                return;
              }
            }
          } catch (e) {
            // ignore secondary check errors
          }
        }

        if (attempts >= 24) { // 60 seconds (24 * 2.5s)
          clearInterval(pollingRef.current);
          setPaymentStatus('timeout');
          setPaymentMessage('We did not receive instant M-Pesa confirmation. If you approved the transaction on your phone, your payment will automatically reflect shortly.');
          setPaymentSteps([
            { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
            { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
            { key: 'pin', label: 'Check Mobile Phone', status: 'done' },
            { key: 'confirm', label: 'Waiting for Network Callback', status: 'warning' },
            { key: 'outcome', label: 'Pending Network Confirmation', status: 'warning' },
          ]);
        }
      } catch (err) {
        console.error('Error checking STK status:', err);
      }
    }, 2500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!amount) {
      toast.error('Please enter contribution amount');
      return;
    }

    if (!phoneNumber) {
      toast.error('Please enter phone number');
      return;
    }

    if (isAdmin && !selectedMember) {
      toast.error('Please select a member');
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (selectedDrives.length > 0) {
      const missingAmounts = selectedDrives.filter(drive => !driveAmounts[drive.id] || parseFloat(driveAmounts[drive.id]) <= 0);
      if (missingAmounts.length > 0) {
        toast.error(`Please enter amounts for: ${missingAmounts.map(d => d.title).join(', ')}`);
        return;
      }
    }

    // Open waiting modal state
    setShowPaymentModal(true);
    setPaymentStatus('submitting');
    setPaymentMessage('Initiating M-Pesa payment request...');
    setPaymentError('');
    setReceiptNumber('');
    setPaymentSteps([
      { key: 'init', label: 'Twachanga Contribution Request', status: 'active' },
      { key: 'stk', label: 'M-Pesa STK Push Prompt', status: 'pending' },
      { key: 'pin', label: 'Enter M-Pesa PIN on Mobile Phone', status: 'pending' },
      { key: 'confirm', label: 'Twachanga Payment Verification', status: 'pending' },
      { key: 'outcome', label: 'Payment Result', status: 'idle' },
    ]);

    let payload = {
      amount: amountValue,
      notes: notes,
      phone_number: phoneNumber,
      member_id: selectedMember?.id,
    };

    if (welfarePlan) {
      payload.welfare_plan_id = welfarePlan.id;
      payload.notes = notes || `Welfare - ${welfarePlan.name}`;
    } else if (selectedDrives.length > 0) {
      const driveDetails = selectedDrives.map(drive => {
        const driveAmount = driveAmounts[drive.id] || '0';
        return `${drive.title}: KSh ${parseFloat(driveAmount).toLocaleString()}`;
      }).join(', ');
      
      payload.notes = notes ? `${notes} - ${driveDetails}` : driveDetails;
      payload.drive_contributions = selectedDrives.map(drive => ({
        drive_id: drive.id,
        amount: parseFloat(driveAmounts[drive.id] || 0)
      }));
    } else {
      payload.drive_id = null;
    }

    payContribution(payload, {
      onSuccess: (response) => {
        const stkData = response?.data || response;
        const checkoutRequestId =
          stkData?.CheckoutRequestID ||
          stkData?.data?.CheckoutRequestID ||
          response?.CheckoutRequestID ||
          response?.data?.CheckoutRequestID ||
          response?.data?.data?.CheckoutRequestID;

        if (checkoutRequestId) {
          setPaymentStatus('waiting_pin');
          setPaymentMessage(`STK Push prompt sent to ${phoneNumber}. Please check your phone and enter your M-Pesa PIN.`);
          setPaymentSteps([
            { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
            { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
            { key: 'pin', label: 'Please check your phone & enter PIN', status: 'active' },
            { key: 'confirm', label: 'Waiting for M-Pesa Callback', status: 'pending' },
            { key: 'outcome', label: 'Payment Result', status: 'idle' },
          ]);
          startStkPolling(checkoutRequestId);
        } else {
          setPaymentStatus('completed');
          setPaymentMessage(response?.message || 'Contribution payment initiated successfully!');
          setPaymentSteps([
            { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
            { key: 'stk', label: 'M-Pesa STK Push Sent', status: 'done' },
            { key: 'pin', label: 'PIN Request Sent', status: 'done' },
            { key: 'confirm', label: 'Completed', status: 'done' },
            { key: 'outcome', label: 'Payment Initiated', status: 'success' },
          ]);
          setTimeout(() => {
            setShowPaymentModal(false);
            navigate('/contributions');
          }, 2000);
        }
      },
      onError: (error) => {
        const errMsg = error?.response?.data?.message || 
                       error?.response?.data?.error || 
                       error?.message || 
                       'Failed to initiate payment. Please verify phone number and try again.';
        setPaymentStatus('failed');
        setPaymentError(errMsg);
        setPaymentMessage('M-Pesa STK Push request could not be sent.');
        setPaymentSteps([
          { key: 'init', label: 'Twachanga Contribution Request', status: 'done' },
          { key: 'stk', label: 'M-Pesa STK Push Failed', status: 'failed' },
          { key: 'pin', label: 'Enter M-Pesa PIN', status: 'idle' },
          { key: 'confirm', label: 'Verification', status: 'idle' },
          { key: 'outcome', label: 'Payment Failed', status: 'failed' },
        ]);
      }
    });
  };

  const closePaymentModal = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setShowPaymentModal(false);
    if (paymentStatus === 'completed') {
      navigate('/contributions');
    }
  };

  return (
    <div className="py-8 min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors">
      <div className="px-4 mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center mb-4 text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Pay Contribution</h1>
          <p className="mt-2 text-gray-600 dark:text-slate-400">Make a contribution payment via M-Pesa</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Member Selection for Admin */}
          {isAdmin && (
            <div className="p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/60">
              <div className="flex items-center mb-4">
                <User size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Member *</h2>
              </div>
              <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">Choose the member making this contribution</p>
              
              {selectedMember ? (
                <div className="p-4 mb-4 bg-green-50 dark:bg-emerald-950/40 rounded-lg border-2 border-green-200 dark:border-emerald-800">
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-3 w-10 h-10 bg-green-100 dark:bg-emerald-900/60 rounded-full">
                      <span className="font-semibold text-green-800 dark:text-emerald-300">
                        {selectedMember.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-slate-100">{selectedMember.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-slate-400">{selectedMember.phone_number}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelectedMember(null); setMemberSearch(''); }}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-emerald-300 bg-green-100 dark:bg-emerald-900/60 rounded-full">
                      <CheckCircle2 className="mr-1 w-3.5 h-3.5" />
                      Member Selected
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Search size={20} className="text-gray-400 dark:text-slate-500" />
                    </div>
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search by member name or phone number..."
                      className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 dark:placeholder-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-md border border-gray-300 dark:border-slate-700 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-slate-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  {memberSearch.length > 0 && (
                    <div className="overflow-y-auto max-h-48 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-lg">
                      {filteredMembers.length === 0 ? (
                        <div className="p-4 text-center">
                          <p className="text-gray-500 dark:text-slate-400">No members found</p>
                          <p className="text-sm text-gray-400 dark:text-slate-500">Try a different search term</p>
                        </div>
                      ) : (
                        filteredMembers.slice(0, 10).map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => {
                              setSelectedMember(m);
                              setMemberSearch('');
                              setPhoneNumber(m.phone_number || '');
                            }}
                            className="flex items-center p-3 w-full border-b border-gray-100 dark:border-slate-700/60 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50"
                          >
                            <div className="flex justify-center items-center mr-3 w-8 h-8 bg-blue-100 dark:bg-blue-900/60 rounded-full">
                              <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                                {m.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </span>
                            </div>
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900 dark:text-slate-100">{m.name}</p>
                              <p className="text-sm text-gray-600 dark:text-slate-400">{m.phone_number}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {welfarePlan && (
            <div className="p-4 rounded-xl border border-green-200 bg-green-50 dark:bg-slate-800 dark:border-green-700/40 text-sm text-green-900 dark:text-green-200">
              Paying into welfare plan <strong>{welfarePlan.name}</strong>. The M-Pesa prompt comes from the paybill set for this plan.
            </div>
          )}

          {/* Contribution Categories Selection */}
          <div className={`p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/60 ${welfarePlan ? 'hidden' : ''}`}>
            <div className="flex items-center mb-4">
              <Target size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contribution Categories</h2>
            </div>
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">Select one or more contribution categories to pay for them at once.</p>
            
            {/* Selected Drives Summary */}
            {selectedDrives.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-slate-100 text-md">Selected Categories ({selectedDrives.length})</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDrives([]);
                      setDriveAmounts({});
                    }}
                    className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                
                <div className="space-y-3">
                  {selectedDrives.map((drive) => (
                    <div key={drive.id} className="p-3 bg-green-50 dark:bg-emerald-950/40 rounded-lg border border-green-200 dark:border-emerald-800">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="flex justify-center items-center mr-3 w-8 h-8 bg-green-100 dark:bg-emerald-900/60 rounded-full">
                            <Target size={16} className="text-green-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-slate-100">{drive.title}</h4>
                            {drive.amount_per_member && (
                              <p className="text-xs text-gray-600 dark:text-slate-400">
                                Suggested: KSh {drive.amount_per_member.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDrive(drive.id)}
                          className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="ml-11">
                        <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">
                          Amount for {drive.title} (KES)
                        </label>
                        <input
                          type="number"
                          value={driveAmounts[drive.id] || ''}
                          onChange={(e) => updateDriveAmount(drive.id, e.target.value)}
                          placeholder={drive.amount_per_member ? `Suggested: ${drive.amount_per_member}` : "Enter amount"}
                          min="0"
                          step="0.01"
                          className="block px-3 py-2 w-full leading-5 placeholder-gray-500 dark:placeholder-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-md border border-gray-300 dark:border-slate-700 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Amount Display */}
                {selectedDrives.length > 0 && (
                  <div className="p-4 mt-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <Calculator size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
                        <span className="font-semibold text-blue-900 dark:text-blue-200">Total Amount:</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                        KSh {totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Available Drives */}
            <div className="space-y-2">
              {drives.length === 0 ? (
                <div className="p-4 text-center bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Target size={24} className="mx-auto mb-2 text-amber-600 dark:text-amber-400" />
                  <p className="font-medium text-amber-800 dark:text-amber-200">No Active Categories</p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">No contribution categories are currently active.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {drives.map((drive) => {
                    const isSelected = selectedDrives.find(d => d.id === drive.id);
                    return (
                      <button
                        key={drive.id}
                        type="button"
                        onClick={() => handleDriveSelection(drive)}
                        className={`p-4 border-2 rounded-lg transition-colors text-left ${
                          isSelected 
                            ? 'bg-green-50 dark:bg-emerald-950/40 border-green-500 dark:border-emerald-500' 
                            : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/30'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-slate-100">{drive.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            drive.type === 'regular' ? 'bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300' :
                            drive.type === 'emergency' ? 'bg-red-100 dark:bg-red-950/60 text-red-800 dark:text-red-300' :
                            drive.type === 'project' ? 'bg-green-100 dark:bg-emerald-950/60 text-green-800 dark:text-emerald-300' :
                            drive.type === 'celebration' ? 'bg-purple-100 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300' :
                            drive.type === 'one_off' ? 'bg-orange-100 dark:bg-amber-950/60 text-orange-800 dark:text-amber-300' :
                            'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-300'
                          }`}>
                            {drive.type || 'Custom'}
                          </span>
                        </div>
                        {drive.amount_per_member && (
                          <p className="mb-1 text-sm text-gray-600 dark:text-slate-400">
                            KSh {drive.amount_per_member.toLocaleString()} per member
                          </p>
                        )}
                        {drive.target_amount && (
                          <p className="text-sm text-gray-500 dark:text-slate-500">
                            Target: KSh {drive.target_amount.toLocaleString()}
                          </p>
                        )}
                        {isSelected && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 dark:text-emerald-300 bg-green-100 dark:bg-emerald-950/60 rounded-full">
                              <CheckCircle2 className="mr-1 w-3 h-3" />
                              Selected
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Phone Number */}
          <div className="p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/60">
            <div className="flex items-center mb-4">
              <Phone size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Phone Number *
              </h2>
            </div>
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
              {isAdmin ? "M-Pesa registered phone number for member's payment" : 'Your M-Pesa registered phone number'}
            </p>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. 0712345678 or 254712345678"
              className="block px-3 py-2 w-full leading-5 placeholder-gray-500 dark:placeholder-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-md border border-gray-300 dark:border-slate-700 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Amount */}
          <div className="p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/60">
            <div className="flex items-center mb-4">
              <DollarSign size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Amount (KES) *</h2>
            </div>
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
              {selectedDrives.length > 0 
                ? `Total amount (auto-calculated from selected categories)`
                : 'Enter the contribution amount'
              }
            </p>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter contribution amount"
              min="0"
              step="1"
              className={`block w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md leading-5 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                selectedDrives.length > 0 ? 'bg-gray-100 dark:bg-slate-900/60 text-gray-500 dark:text-slate-400 cursor-not-allowed' : ''
              }`}
              readOnly={selectedDrives.length > 0}
            />
          </div>

          {/* Notes */}
          <div className="p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/60">
            <div className="flex items-center mb-4">
              <FileText size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notes (Optional)</h2>
            </div>
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
              Additional information or reference notes for this contribution
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Monthly contribution for January"
              rows={3}
              className="block px-3 py-2 w-full leading-5 placeholder-gray-500 dark:placeholder-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-md border border-gray-300 dark:border-slate-700 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* M-Pesa Notice */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start">
              <Info size={20} className="flex-shrink-0 mt-0.5 mr-2 text-blue-600 dark:text-blue-400" />
              <div>
                <h3 className="mb-1 text-sm font-semibold text-blue-800 dark:text-blue-200">M-Pesa Express Payment</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  An M-Pesa prompt will be sent directly to phone <span className="font-semibold">{phoneNumber || 'number'}</span>. You will see real-time payment progress here.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 rounded-md border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPayingContribution || (isAdmin && !selectedMember)}
              className="flex items-center px-6 py-2 text-white bg-green-600 hover:bg-green-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm transition-colors"
            >
              {isPayingContribution ? (
                <>
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={20} className="mr-2" />
                  {selectedDrives.length > 0 ? `Pay KSh ${totalAmount.toLocaleString()} with M-Pesa` : 'Pay with M-Pesa'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Real-time M-Pesa Payment Status Modal Card */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 dark:border-slate-800">
            {/* Modal Header */}
            <div className="bg-slate-900 dark:bg-slate-950 text-white px-6 py-4 flex justify-between items-center border-b border-slate-800">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-lg text-white">M-Pesa Payment Status</h3>
              </div>
              {(paymentStatus === 'completed' || paymentStatus === 'failed' || paymentStatus === 'timeout') && (
                <button
                  onClick={closePaymentModal}
                  className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            <div className="p-6 space-y-6">
              {/* Payment Details Banner */}
              <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex justify-between items-center">
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Amount to Pay</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-emerald-400">KSh {parseFloat(amount || 0).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Phone Number</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{phoneNumber}</p>
                </div>
              </div>

              {/* Status Banner */}
              {paymentStatus === 'submitting' && (
                <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 p-4 rounded-xl flex items-center space-x-3">
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
                  <span className="text-sm font-medium">{paymentMessage}</span>
                </div>
              )}

              {paymentStatus === 'waiting_pin' && (
                <div className="bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 p-4 rounded-xl flex items-center space-x-3 animate-pulse">
                  <Phone className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 animate-bounce" />
                  <div>
                    <p className="text-sm font-bold">Check Your Phone Now!</p>
                    <p className="text-xs text-amber-800 dark:text-amber-300">{paymentMessage}</p>
                  </div>
                </div>
              )}

              {paymentStatus === 'completed' && (
                <div className="bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200 p-4 rounded-xl flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold">Payment Successful!</p>
                    <p className="text-xs text-emerald-800 dark:text-emerald-300">{paymentMessage}</p>
                    {receiptNumber && (
                      <p className="text-xs font-mono mt-1 bg-emerald-100 dark:bg-emerald-900/60 inline-block px-2 py-0.5 rounded text-emerald-900 dark:text-emerald-200 font-semibold">
                        Receipt: {receiptNumber}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(paymentStatus === 'failed' || paymentError) && (
                <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-200 p-4 rounded-xl flex items-start space-x-3">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold">Payment Request Unsuccessful</p>
                    <p className="text-xs text-red-800 dark:text-red-300 mt-0.5">{paymentError || paymentMessage}</p>
                  </div>
                </div>
              )}

              {paymentStatus === 'timeout' && (
                <div className="bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 p-4 rounded-xl flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold">Pending Confirmation</p>
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">{paymentMessage}</p>
                  </div>
                </div>
              )}

              {/* Visual Step Progress Tracker */}
              <div className="space-y-3 pt-2">
                <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Transaction Steps</p>
                <div className="flex flex-col space-y-3">
                  {paymentSteps.map((step, idx) => (
                    <div key={step.key} className="flex items-center space-x-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 transition-colors ${
                        step.status === 'done' || step.status === 'success' ? 'bg-emerald-600 text-white' :
                        step.status === 'active' ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' :
                        step.status === 'failed' ? 'bg-red-600 text-white' :
                        step.status === 'warning' ? 'bg-amber-500 text-white' :
                        'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 border border-gray-200 dark:border-slate-700'
                      }`}>
                        {step.status === 'done' || step.status === 'success' ? '✓' :
                         step.status === 'failed' ? '✕' :
                         step.status === 'active' ? <Loader2 size={14} className="animate-spin" /> :
                         idx + 1}
                      </div>
                      <span className={`text-sm font-medium ${
                        step.status === 'done' || step.status === 'success' ? 'text-emerald-700 dark:text-emerald-400 font-semibold' :
                        step.status === 'active' ? 'text-blue-700 dark:text-blue-400 font-semibold' :
                        step.status === 'failed' ? 'text-red-700 dark:text-red-400 font-semibold' :
                        step.status === 'warning' ? 'text-amber-700 dark:text-amber-400 font-semibold' :
                        'text-gray-400 dark:text-slate-500'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-gray-100 dark:border-slate-800 flex justify-end space-x-3">
                {paymentStatus === 'failed' && (
                  <button
                    onClick={() => {
                      if (pollingRef.current) clearInterval(pollingRef.current);
                      setShowPaymentModal(false);
                      setPaymentStatus('idle');
                    }}
                    className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Try Again
                  </button>
                )}

                {paymentStatus === 'completed' && (
                  <button
                    onClick={closePaymentModal}
                    className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    Done & View Contributions
                  </button>
                )}

                {(paymentStatus === 'waiting_pin' || paymentStatus === 'submitting' || paymentStatus === 'timeout') && (
                  <button
                    onClick={closePaymentModal}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors"
                  >
                    Close Window
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}